import { GetStaticPaths } from 'next';

import { TypeAreaSkeleton, TypeCitySkeleton, TypeJobAdvert, TypeJobAdvertSkeleton } from 'generated/contentful';

import { groupBy, range } from 'lodash';

import { createClient } from 'common/lib/contentful';

import PaginatedCityJobsBoardPage, {
  CityJobsBoardParams,
  PAGE_LIMIT,
  getStaticProps as getStaticPropsFn
} from '../index';

export const getStaticPaths: GetStaticPaths<CityJobsBoardParams> = async () => {
  const client = createClient();

  const cities = await client.withoutUnresolvableLinks.getEntries<TypeCitySkeleton>({
    content_type: 'city',
    limit: 1000
  });

  const areas = await client.withoutUnresolvableLinks.getEntries<TypeAreaSkeleton>({
    content_type: 'area',
    limit: 1000,
    include: 2
  });

  const jobAdverts = await client.withoutUnresolvableLinks.getEntries<TypeJobAdvertSkeleton>({
    content_type: 'jobAdvert',
    limit: 100,
    order: ['fields.title']
  });

  // Get the number of items listed under each city.
  const items = [...areas.items, ...cities.items]
    .flatMap(location => jobAdverts.items.map(jobAdvert => ({ jobAdvert, location })))
    .map(({ jobAdvert, location }) => ({
      jobAdvert: jobAdvert as TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>,
      citySlug: 'city' in location.fields ? location.fields.city?.fields.slug : location.fields.slug
    }));

  // Group the items by city.
  const grouped = groupBy(items, item => item.citySlug);

  // Create all paginated paths, starting from page 2.
  const paths = cities.items
    .map(city => ({ city, numItems: grouped[city.fields.slug].length }))
    .flatMap(({ city, numItems }) =>
      range(2, Math.ceil(numItems / PAGE_LIMIT) + 1)
        .map(page => ({ page, city }))
        .map(({ city, page }) => ({
          params: {
            city: city.fields.slug,
            page: page.toString()
          }
        }))
    );

  return {
    paths,
    fallback: false
  };
};

// Use the same `getStaticProps` function as the CityJobsBoardPage
export const getStaticProps = getStaticPropsFn;

// Use the CityJobsBoardPage component
export default PaginatedCityJobsBoardPage;
